import React, { useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import ListItem from "@material-ui/core/ListItem";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItemText from "@material-ui/core/ListItemText";

import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import VpnKeyIcon from "@material-ui/icons/VpnKey";

import grey from "@material-ui/core/colors/grey";
import Typography from "@material-ui/core/Typography";
import { green, red, yellow } from "@material-ui/core/colors";
import Tooltip from "@material-ui/core/Tooltip";
import Grid from "@material-ui/core/Grid";

const useStyles = makeStyles(theme => ({
  itemContent: {
    height: "auto",
    fontSize: "1.25rem",
    borderBottom: "solid",
    borderBottomColor: grey[200],
    backgroundColor: "white",
  },

  listItemText: {
    textTransform: "none",
  },

  contentTitle: {
    fontSize: "2.00rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(4),
  },

  labelAndValue: {
    "& label": {
      fontWeight: "bold",
      paddingRight: theme.spacing(2),
    },
  },

  active: {
    textTransform: "uppercase",
    color: green[500],
  },

  inactive: {
    textTransform: "uppercase",
    color: red[500],
  },

  restricted: {
    textTransform: "uppercase",
    color: yellow[500],
  },

  actionContainer: {
    "& button": {
      margin: "10px 0",
    },
  },
}));

const ApplicationAccountListItem = ({
  account,
  selected,
  onEditClick,
  onDeactivateClick,
  onActivateClick,
  onKeysClick,
  onSelect,
}) => {
  const classes = useStyles();

  const handleActiveClicked = useCallback(() => {
    onSelect(account);
    onActivateClick(account);
  }, [onSelect, onActivateClick, account]);

  const handleDeactivateClicked = useCallback(() => {
    onSelect(account);
    onDeactivateClick(account);
  }, [onSelect, onDeactivateClick, account]);

  const handleEditClicked = useCallback(() => {
    onSelect(account);
    onEditClick(account);
  }, [onSelect, onEditClick, account]);

  const handleShowKeysClicked = useCallback(() => {
    onSelect(account);
    onKeysClick(account);
  }, [onSelect, onKeysClick, account]);

  return (
    <ListItem
      selected={selected}
      classes={{ root: classes.itemContent }}
      key={account.applicationId}
      onClick={() => onSelect(account)}
    >
      <ListItemText
        classes={{ root: classes.listItemText }}
        disableTypography={true}
        primary={
          <React.Fragment>
            <div className={classes.contentTitle}>
              {account.applicationName}
            </div>
          </React.Fragment>
        }
        secondary={
          <Grid container justify="flex-start" spacing={2}>
            <Grid item sm={5}>
              <div className={classes.labelAndValue}>
                <label>Application Account Id:</label>
                {account.applicationId}
              </div>
            </Grid>

            <Grid item sm={3}>
              <Grid container spacing={1} direction="column">
                <Grid item>
                  {account.active ? (
                    <Typography
                      component="span"
                      variant="h4"
                      classes={{ root: classes.active }}
                    >
                      Active
                    </Typography>
                  ) : (
                    <Typography
                      component="span"
                      variant="h4"
                      classes={{ root: classes.inactive }}
                    >
                      Inactive
                    </Typography>
                  )}
                </Grid>
                {account.accessRestricted && (
                  <Grid item>
                    <Typography
                      component="span"
                      variant="h4"
                      classes={{ root: classes.restricted }}
                    >
                      Restricted
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item sm={4}>
              <Grid container direction="column" spacing={1}>
                <Grid item>
                  <div className={classes.labelAndValue}>
                    <label>Created By:</label>
                    {account.createdByUsername}
                  </div>
                </Grid>
                <Grid item>
                  <div className={classes.labelAndValue}>
                    <label>Date Created:</label>
                    {account.dateCreated}
                  </div>
                </Grid>
                <Grid item>
                  <div className={classes.labelAndValue}>
                    <label>Updated By:</label>
                    {account.updatedByUsername}
                  </div>
                </Grid>
                <Grid item>
                  <div className={classes.labelAndValue}>
                    <label>Date Updated:</label>
                    {account.dateUpdated}
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        }
      />
      <ListItemSecondaryAction classes={{ root: classes.actionContainer }}>
        <Tooltip title="Edit Account">
          <IconButton
            autoFocus={selected}
            edge="end"
            aria-label="edit"
            onClick={handleEditClicked}
          >
            <EditIcon fontSize="large" />
            <Typography variant="srOnly">Edit Account</Typography>
          </IconButton>
        </Tooltip>

        <Tooltip title="Show Keys">
          <IconButton
            edge="end"
            aria-label="show keys"
            onClick={handleShowKeysClicked}
          >
            <VpnKeyIcon fontSize="large" />
            <Typography variant="srOnly">Show Keys</Typography>
          </IconButton>
        </Tooltip>

        {account.active ? (
          <Tooltip title="Deactivate Account">
            <IconButton
              edge="end"
              aria-label="deactivate"
              onClick={handleDeactivateClicked}
            >
              <RemoveCircleOutlineIcon fontSize="large" />
              <Typography variant="srOnly">Deactivate Account</Typography>
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Activate Account">
            <IconButton
              edge="end"
              aria-label="activate"
              onClick={handleActiveClicked}
            >
              <CheckCircleOutlineIcon fontSize="large" />
              <Typography variant="srOnly">Activate Account</Typography>
            </IconButton>
          </Tooltip>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export default ApplicationAccountListItem;
